import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingCart, X, ArrowRight, Crown, Trash2, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart, formatInr } from "@/lib/cart";
import { PRICING } from "@/lib/pricing";
import { useCourses } from "@/lib/useCourses";

export default function StickyCart() {
  const { cart, clear, removeCourse } = useCart();
  const [open, setOpen] = useState(false);
  const courses = useCourses();

  if (cart.isEmpty) return null;

  const titleFor = (slug) => courses.find((c) => c.slug === slug)?.title || slug;
  const coverFor = (slug) => courses.find((c) => c.slug === slug)?.cover;

  const label =
    cart.packageName ||
    `${cart.n} course${cart.n > 1 ? "s" : ""}`;

  return (
    <>
      <button
        data-testid="sticky-cart-btn"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 sm:bottom-5 sm:right-5 max-sm:bottom-20 z-40 inline-flex items-center gap-3 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] pl-4 pr-5 py-3 shadow-2xl shadow-[#0F172A]/30 hover:-translate-y-1 transition-transform"
      >
        <span className="relative">
          <ShoppingCart className="h-4 w-4" />
          <span
            data-testid="sticky-cart-count"
            className="absolute -top-2 -right-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#06B6D4] text-[#0F172A] text-[10px] px-1.5 font-bold border-2 border-[#0F172A] dark:border-white"
          >
            {cart.n}
          </span>
        </span>
        <span className="flex flex-col items-start leading-none">
          <span className="text-[10px] uppercase tracking-[0.2em] opacity-70">
            {label}
          </span>
          <span className="mt-0.5 text-sm font-semibold">
            {formatInr(cart.price)}
          </span>
        </span>
        <ArrowRight className="h-4 w-4" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#020617]/70 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && setOpen(false)}
            data-testid="sticky-cart-drawer"
          >
            <motion.aside
              initial={{ x: 60, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 60, opacity: 0 }}
              className="absolute top-0 right-0 h-full w-full max-w-sm bg-white dark:bg-[#0F172A] border-l border-slate-200/80 dark:border-white/10 p-6 flex flex-col"
            >
              <div className="flex items-center justify-between">
                <div className="font-display text-lg tracking-tight font-semibold text-[#0F172A] dark:text-white inline-flex items-center gap-2">
                  {cart.packageId === "premium" && (
                    <Crown className="h-4 w-4 text-[#06B6D4]" />
                  )}
                  Your Cart
                </div>
                <button
                  data-testid="sticky-cart-close"
                  onClick={() => setOpen(false)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-full border border-slate-200 dark:border-white/10"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {cart.packageName && (
                <div
                  data-testid="sticky-cart-package-applied"
                  className="mt-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300"
                >
                  🎉 {cart.packageName} automatically applied
                  {cart.savings > 0 && <> — you save {formatInr(cart.savings)}</>}
                </div>
              )}

              {cart.nextTier && (cart.nextTier.saveAmount || cart.nextTier.saveFromCurrent) && (
                <div
                  data-testid="sticky-cart-upgrade-prompt"
                  className="mt-3 rounded-xl bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 inline-flex items-start gap-2"
                >
                  <TrendingUp className="h-3.5 w-3.5 mt-0.5 flex-none" />
                  <span>
                    {cart.n === 4 ? "🏆" : "🔥"} {cart.nextTier.label}
                    {cart.nextTier.saveFromCurrent > 0 && (
                      <> — save {formatInr(cart.nextTier.saveFromCurrent)} instantly</>
                    )}
                  </span>
                </div>
              )}

              <div className="mt-4 flex-1 overflow-y-auto space-y-3">
                {cart.courses.map((s) => (
                  <div
                    key={s}
                    data-testid={`sticky-row-${s}`}
                    className="flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-white/10 p-2.5"
                  >
                    <img
                      src={coverFor(s)}
                      alt=""
                      className="h-12 w-12 rounded-lg object-cover flex-none"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0F172A] dark:text-white truncate">
                        {titleFor(s)}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {formatInr(PRICING.coursePrice)}
                      </div>
                    </div>
                    <button
                      onClick={() => removeCourse(s)}
                      data-testid={`sticky-remove-${s}`}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-rose-500 hover:bg-rose-500/10"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-5 border-t border-slate-200 dark:border-white/10 pt-4 space-y-1.5 text-sm">
                <div className="flex items-center justify-between text-slate-500 dark:text-slate-400">
                  <span>Original</span>
                  <span className={cart.savings > 0 ? "line-through" : ""}>
                    {formatInr(cart.original)}
                  </span>
                </div>
                {cart.savings > 0 && (
                  <div className="flex items-center justify-between text-emerald-600 dark:text-emerald-400">
                    <span>Discount</span>
                    <span>− {formatInr(cart.savings)}</span>
                  </div>
                )}
                <div className="flex items-end justify-between pt-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                    You pay
                  </div>
                  <div className="font-display text-2xl font-semibold tracking-tight text-[#0F172A] dark:text-white">
                    {formatInr(cart.price)}
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-2">
                <Link
                  to="/cart"
                  data-testid="sticky-cart-checkout"
                  onClick={() => setOpen(false)}
                  className="inline-flex justify-center items-center gap-2 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform"
                >
                  Continue to Checkout <ArrowRight className="h-4 w-4" />
                </Link>
                <button
                  type="button"
                  data-testid="sticky-cart-clear"
                  onClick={() => {
                    clear();
                    setOpen(false);
                  }}
                  className="inline-flex justify-center items-center gap-1 text-xs text-slate-500 hover:text-rose-500 py-2"
                >
                  Clear cart
                </button>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
